import { AbstractControl, FormArray, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { DateFormatter } from './date-utils';
import { Cpf } from './utils/cpf';
import { isTimeIn24FormatValid } from './utils/dates/dates';
import { Cns } from './utils/cns';
import { ZipCode } from './utils/zip-code';

interface IFieldError {
  [key: string]: any;
}

const allowValidation = (control: AbstractControl): boolean => {
  return control.touched && !control.disabled;
};

export class LibValidators {
  static required(message: string): ValidatorFn {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      return Validators.required(control)
        ? {
            requiredValidation: message,
          }
        : null;
    };
  }

  static requiredWithoutWhitespace(message: string): ValidatorFn {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      const value = control.value;
      const isEmpty = !value || value.trim().length === 0;

      return isEmpty ? { requiredValidation: message } : null;
    };
  }

  static equal(requiredValue: string | boolean, message: string): ValidatorFn {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      return control.value !== requiredValue
        ? {
            equalValidation: message,
          }
        : null;
    };
  }

  static minLength(minLength: number, message: string) {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      return control.value?.length < minLength
        ? {
            minLengthValidation: message,
          }
        : null;
    };
  }

  static maxLength(maxLength: number, message: string) {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      return control.value?.length > maxLength
        ? {
            maxLengthValidation: message,
          }
        : null;
    };
  }

  static pattern(regexp: RegExp, message: string) {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      return !regexp.test(control.value)
        ? {
            patternValidation: message,
          }
        : null;
    };
  }

  static email(message: string): ValidatorFn {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      // This validator should validate only existed value, so do nothing if no value
      if (!control.value) {
        return null;
      }

      // https://emailregex.com/
      const regexp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      const email = control.value;

      const isValid = regexp.test(email);

      return !isValid
        ? {
            emailValidation: message,
          }
        : null;
    };
  }

  static optionalEmail(message: string): ValidatorFn {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      if (!control.value) {
        return null;
      }

      const regexp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const email = control.value;
      const isValid = regexp.test(email);

      return !isValid ? { optionalEmailValidation: message } : null;
    };
  }

  static cpfRequiredIfEmailEmpty(message: string): ValidatorFn {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      const emailControl = control.get('email');
      const cpfControl = control.get('cpf');

      if (!emailControl || !cpfControl) {
        return null;
      }

      if (
        (!emailControl.value || emailControl.value.trim() === '') &&
        (!cpfControl.value || cpfControl.value.trim() === '')
      ) {
        return { cpfRequiredIfEmailEmpty: message };
      }

      return null;
    };
  }

  static validateRequiredCpfOrEmailOrCns(message: string): ValidatorFn {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      const emailControl = control.get('email');
      const cpfControl = control.get('cpf');
      const cnsControl = control.get('cns');

      if (!emailControl || !cpfControl || !cnsControl) {
        return null;
      }

      if (
        (!emailControl.value || emailControl.value.trim() === '') &&
        (!cpfControl.value || cpfControl.value.trim() === '') &&
        (!cnsControl.value || cnsControl.value.trim() === '')
      ) {
        return { cpfOrEmailOrCnsValidation: message };
      }

      return null;
    };
  }

  static sameValueAs(target: AbstractControl, message: string): ValidatorFn {
    return (control: AbstractControl): IFieldError | null => {
      if (!allowValidation(control)) {
        return null;
      }

      const valid = control.value && control.value === target.value;

      return !valid
        ? {
            sameValueAs: message,
          }
        : null;
    };
  }

  static date(format: string, message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }

      // This validator should validate only existed value, so do nothing if no value
      if (!control.value) {
        return null;
      }

      return DateFormatter.isStringIsValidDate(control.value, format) ? null : { dateValidation: message };
    };
  }

  static isoDateString(message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }

      // This validator should validate only existed value, so do nothing if no value
      if (!control.value) {
        return null;
      }

      const date = new Date(control.value);
      return !isNaN(date.getDate()) ? null : { dateValidation: message };
    };
  }

  static timeIn24HoursFormat(format: string = 'HH:MM', message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }

      // This validator should validate only existed value, so do nothing if no value
      if (!control.value) {
        return null;
      }

      return isTimeIn24FormatValid(control.value, format) ? null : { timeIn24HoursFormat: message };
    };
  }

  static integer(message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }

      const intValue = parseInt(control.value, 10);
      const floatValue = parseFloat(control.value);
      const valid = !isNaN(intValue) && intValue.toString() === control.value && intValue === floatValue;

      return !valid
        ? {
            positiveInteger: message,
          }
        : null;
    };
  }

  static numberOnly(message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }
      const valid = (control.value || '').match('^[0-9]*$');

      return !valid || valid[0] === ''
        ? {
            numberOnly: message,
          }
        : null;
    };
  }

  static rangeNumber(from: number, to: number, message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }

      const numberValue = parseFloat(control.value);
      const valid =
        !isNaN(numberValue) && numberValue.toString() === control.value && numberValue >= from && numberValue <= to;

      return !valid
        ? {
            rangeNumber: message,
          }
        : null;
    };
  }

  static cpf(message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }

      // This validator should validate only existed value, so do nothing if no value
      if (!control.value) {
        return null;
      }

      return Cpf.isValid(control.value) ? null : { cpfValidation: message };
    };
  }

  static cns(message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control) || !control.value) {
        return null;
      }

      // This validator should validate only existed value, so do nothing if no value
      if (!control.value) {
        return null;
      }

      return Cns.isValid(control.value) ? null : { cnsValidation: message };
    };
  }

  static enumValue<E extends Record<string, string | number>>(enumToCheck: E, message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }

      // This validator should validate only existed value, so do nothing if no value
      if (control.value === null || control.value === undefined) {
        return null;
      }

      return Object.values(enumToCheck).includes(control.value) ? null : { oneOfEnumValidation: message };
    };
  }

  // Same as enumValue, but the allowed values are resolved at validation time (e.g. a list loaded remotely).
  static oneOfValues(getAllowedValues: () => (string | number)[], message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) {
        return null;
      }

      // This validator should validate only existed value, so do nothing if no value
      if (control.value === null || control.value === undefined) {
        return null;
      }

      return getAllowedValues().includes(control.value) ? null : { oneOfEnumValidation: message };
    };
  }

  static cellphone(message: string): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;

      if (!value || typeof value !== 'string') {
        return null;
      }

      // Remove mask: (11) 99123-1234 => 11991231234
      const digits = value.replace(/\D/g, '');

      // Regex: phone br with 11 digits (ex: 11991231234)
      const isValid = /^(\d{2})9\d{8}$/.test(digits);

      return isValid ? null : { cellphoneValidation: message };
    };
  }

  static cep(message: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!allowValidation(control)) return null;

      if (!control.value) {
        return null;
      }

      const digits = control.value.replace(/\D/g, '');

      return ZipCode.isValid(digits) ? null : { cepValidation: message };
    };
  }
}
